import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useFinancials } from '@/hooks/use-projects';
import type { ProjectDetail } from '@hygge/shared';
import {
  ChevronLeft,
  ChevronRight,
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Percent,
  Users,
} from 'lucide-react';
import { ForecastTable } from './forecast-table';
import { PlanVsActualCard } from './plan-vs-actual-card';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(parseInt(year), parseInt(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatShortMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(parseInt(year), parseInt(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'short' });
}

function navigateMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number);
  const date = new Date(year, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

type SubView = 'summary' | 'forecast';

interface FinancialsTabProps {
  project: ProjectDetail;
}

export function FinancialsTab({ project }: FinancialsTabProps) {
  const [subView, setSubView] = useState<SubView>('summary');

  return (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1 w-fit">
        <button
          type="button"
          onClick={() => setSubView('summary')}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            subView === 'summary'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Summary
        </button>
        <button
          type="button"
          onClick={() => setSubView('forecast')}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            subView === 'forecast'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Forecast
        </button>
      </div>

      {subView === 'summary' ? (
        <SummaryView project={project} />
      ) : (
        <ForecastView project={project} />
      )}
    </div>
  );
}

function ForecastView({ project }: { project: ProjectDetail }) {
  return (
    <div className="space-y-6">
      <ForecastTable project={project} />
    </div>
  );
}

function SummaryView({ project }: { project: ProjectDetail }) {
  const [month, setMonth] = useState(getCurrentMonth);
  const { data, isLoading } = useFinancials(project.id, month);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-60" />
        </div>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-lg border p-12 text-center">
        <p className="text-muted-foreground">No financial data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Month navigator */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonth((m) => navigateMonth(m, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="min-w-[180px] text-center text-lg font-semibold">{formatMonth(month)}</h3>
        <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonth((m) => navigateMonth(m, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryCard
          title="Total Revenue"
          value={formatCurrency(data.totalRevenue)}
          subtitle="Billable Hrs × Real Rate"
          icon={DollarSign}
          color="blue"
        />
        <SummaryCard
          title="Total Cost"
          value={formatCurrency(data.totalCost)}
          subtitle="Real Hrs × Nice Rate"
          icon={TrendingDown}
          color="amber"
        />
        <SummaryCard
          title="Gross Margin"
          value={`${data.grossMargin.toFixed(1)}%`}
          subtitle={data.grossMargin > 0 ? 'Profitable' : 'Negative margin'}
          icon={Percent}
          color={data.grossMargin > 20 ? 'green' : data.grossMargin > 0 ? 'amber' : 'red'}
        />
        <SummaryCard
          title="Utilization"
          value={`${data.utilization.toFixed(1)}%`}
          subtitle={`${data.totalBillableHours.toFixed(0)}h billable / ${data.totalRealHours.toFixed(0)}h real`}
          icon={Users}
          color="purple"
        />
      </div>

      {/* Monthly breakdown chart */}
      {data.monthlyBreakdown.length > 0 && (
        <MonthlyChart breakdown={data.monthlyBreakdown} />
      )}

      {/* Plan vs Actual when forecast data exists, otherwise extrapolation-based forecast */}
      <PlanVsActualCard projectId={project.id} month={month} />

      {data.forecast && (
        <ForecastCard forecast={data.forecast} actualRevenue={data.totalRevenue} actualCost={data.totalCost} />
      )}
    </div>
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'amber' | 'green' | 'red' | 'purple';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400',
    green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-950/30 dark:text-purple-400',
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{title}</p>
          <p className="text-xl font-bold tracking-tight">{value}</p>
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function MonthlyChart({
  breakdown,
}: {
  breakdown: { month: string; revenue: number; cost: number }[];
}) {
  const maxValue = useMemo(
    () => Math.max(...breakdown.flatMap((m) => [m.revenue, m.cost]), 1),
    [breakdown],
  );

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-sm font-semibold">Monthly Breakdown</h4>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
            Revenue
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />
            Cost
          </span>
        </div>
      </div>

      <div className="flex items-end gap-3" style={{ height: 200 }}>
        {breakdown.map((m) => {
          const revenueHeight = maxValue > 0 ? (m.revenue / maxValue) * 180 : 0;
          const costHeight = maxValue > 0 ? (m.cost / maxValue) * 180 : 0;

          return (
            <div key={m.month} className="flex flex-1 flex-col items-center gap-1">
              <div className="flex w-full items-end justify-center gap-1" style={{ height: 180 }}>
                <div
                  className="w-5 rounded-t bg-amber-400 transition-all dark:bg-amber-500"
                  style={{ height: Math.max(costHeight, 2) }}
                  title={`Cost: ${formatCurrency(m.cost)}`}
                />
                <div
                  className="w-5 rounded-t bg-blue-500 transition-all dark:bg-blue-400"
                  style={{ height: Math.max(revenueHeight, 2) }}
                  title={`Revenue: ${formatCurrency(m.revenue)}`}
                />
              </div>
              <span className="text-[10px] font-medium text-muted-foreground">
                {formatShortMonth(m.month)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ForecastCard({
  forecast,
  actualRevenue,
  actualCost,
}: {
  forecast: {
    forecastRevenue: number;
    forecastCost: number;
    revenueVariance: number;
    elapsedBusinessDays: number;
    totalBusinessDays: number;
  };
  actualRevenue: number;
  actualCost: number;
}) {
  const isPositiveVariance = forecast.revenueVariance >= 0;

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="mb-4 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">Extrapolated Forecast</h4>
        <span className="text-xs text-muted-foreground">
          ({forecast.elapsedBusinessDays}/{forecast.totalBusinessDays} business days elapsed)
        </span>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div>
          <p className="text-xs text-muted-foreground">Actual Revenue</p>
          <p className="text-lg font-bold">{formatCurrency(actualRevenue)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Forecast Revenue</p>
          <p className="text-lg font-bold text-muted-foreground">
            {formatCurrency(forecast.forecastRevenue)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Variance</p>
          <div className="flex items-center gap-1.5">
            {isPositiveVariance ? (
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
            <p
              className={`text-lg font-bold ${
                isPositiveVariance ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
              }`}
            >
              {isPositiveVariance ? '+' : ''}
              {forecast.revenueVariance.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-6 border-t pt-4 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">Actual Cost</p>
          <p className="font-semibold">{formatCurrency(actualCost)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Forecast Cost</p>
          <p className="font-semibold text-muted-foreground">{formatCurrency(forecast.forecastCost)}</p>
        </div>
      </div>
    </div>
  );
}
